import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, User, Mail, Phone, MapPin,
  LogOut, Home, ChevronRight, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API, useAuth, authFetch } from '@/App';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5b06bfd4-5798-4290-968c-c27d6c6d2c81/artifacts/krjgv0n0_LOGO01.png";

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  completed: 'bg-blue-100 text-blue-800 border-blue-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const PAYMENT_STATUS_COLORS = {
  unpaid: 'bg-orange-100 text-orange-800 border-orange-200',
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-200'
};

const getClientHeaders = () => {
  const token = sessionStorage.getItem('access_token');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

export default function ClientPortal() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/client/bookings`, {
        headers: getClientHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load your bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const upcoming = bookings.filter(b => b.status === 'pending' || b.status === 'confirmed');
  const past = bookings.filter(b => b.status === 'completed' || b.status === 'cancelled');

  if (loading) {
    return (
      <div className="min-h-screen bg-healing-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-healing-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-healing-burgundy-light font-body">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-healing-cream" data-testid="client-portal">
      {/* Header */}
      <header className="bg-white border-b border-healing-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Healing Hands" className="h-10 w-auto cursor-pointer" onClick={() => navigate('/')} />
              <div>
                <h1 className="font-heading text-lg text-healing-burgundy">My Bookings</h1>
                {user && <p className="text-xs text-healing-burgundy-light">Welcome, {user.name}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="border-healing-border"
                data-testid="client-home-btn"
              >
                <Home className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Home</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigate('/');
                  setTimeout(() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }), 300);
                }}
                className="bg-healing-purple text-white hover:bg-healing-purple-hover border-healing-purple"
                data-testid="client-new-booking-btn"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">New Booking</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-healing-border text-red-600 hover:bg-red-50"
                data-testid="client-logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="admin-card text-center" data-testid="client-stat-total">
            <p className="text-sm text-healing-burgundy-light">Total Bookings</p>
            <p className="text-2xl font-heading text-healing-burgundy">{bookings.length}</p>
          </div>
          <div className="admin-card text-center" data-testid="client-stat-upcoming">
            <p className="text-sm text-healing-burgundy-light">Upcoming</p>
            <p className="text-2xl font-heading text-green-600">{upcoming.length}</p>
          </div>
          <div className="admin-card text-center" data-testid="client-stat-completed">
            <p className="text-sm text-healing-burgundy-light">Completed</p>
            <p className="text-2xl font-heading text-blue-600">{past.filter(b => b.status === 'completed').length}</p>
          </div>
          <div className="admin-card text-center" data-testid="client-stat-cancelled">
            <p className="text-sm text-healing-burgundy-light">Cancelled</p>
            <p className="text-2xl font-heading text-red-600">{past.filter(b => b.status === 'cancelled').length}</p>
          </div>
        </div>

        {bookings.length === 0 ? (
          <div className="admin-card text-center py-16">
            <Calendar className="h-16 w-16 text-healing-border mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-healing-burgundy mb-2">No Bookings Yet</h2>
            <p className="text-healing-burgundy-light mb-6">Book your first massage session with us!</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full px-8"
              data-testid="client-book-first-btn"
            >
              Browse Services
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Upcoming */}
            {upcoming.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-healing-burgundy mb-4">Upcoming Appointments</h2>
                <div className="space-y-4">
                  {upcoming.map(booking => (
                    <BookingCard key={booking.booking_id} booking={booking} />
                  ))}
                </div>
              </div>
            )}

            {/* Past */}
            {past.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-healing-burgundy-light mb-4">Past Appointments</h2>
                <div className="space-y-4">
                  {past.map(booking => (
                    <BookingCard key={booking.booking_id} booking={booking} isPast />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function BookingCard({ booking, isPast = false }) {
  return (
    <div 
      className={`admin-card ${isPast ? 'opacity-75' : ''}`}
      data-testid={`client-booking-${booking.booking_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="space-y-2 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-heading text-lg text-healing-burgundy">{booking.massage_type}</h3>
            <Badge className={STATUS_COLORS[booking.status]}>{booking.status}</Badge>
            <Badge className={PAYMENT_STATUS_COLORS[booking.payment_status]}>
              {booking.payment_status}
              {booking.payment_method && ` (${booking.payment_method})`}
            </Badge>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-healing-burgundy-light">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 flex-shrink-0" />
              <span>{booking.confirmed_date || booking.preferred_date}</span>
              {booking.confirmed_date && <span className="text-green-600 text-xs font-medium">(Confirmed)</span>}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>{booking.confirmed_time || booking.preferred_time}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span>{booking.location_type === 'mobile' ? 'Mobile (Your Location)' : 'Studio'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 flex-shrink-0" />
              <span>{booking.duration} &mdash; ${booking.price}</span>
            </div>
          </div>

          {booking.notes && (
            <p className="text-sm text-healing-burgundy-light bg-healing-cream-alt p-2 rounded">
              Notes: {booking.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
